import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  DollarSign,
  ShoppingBag,
  UtensilsCrossed,
  Clock,
  AlertTriangle,
  ChefHat,
  Truck,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Maximize,
  Minimize,
  RefreshCw,
  WifiOff,
  AlertCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  get_tv_dashboard,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection";

export default function TVDashboard() {
  // State Management
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("online");

  const restroName = retrieveData("restaurant_name");

  // Fetch Dashboard Data
  const fetchTVDashboard = useCallback(async (showLoader = false) => {
    if (showLoader) setIsRefreshing(true);

    const fd = new FormData();

    try {
      const response = await server_post_data(get_tv_dashboard, fd);

      if (!response.data?.error) {
        const msg = response.data?.message;
        setData(msg);
        setLastRefresh(new Date());
        setError(null);
        setConnectionStatus("online");
      } else {
        setError(response.data?.message || "Failed to fetch data");
        setConnectionStatus("error");
      }
    } catch (error) {
      console.error("TV Dashboard API Error:", error);
      setError("Connection failed. Retrying...");
      setConnectionStatus("offline");
    } finally {
      setLoading(false);
      if (showLoader) setIsRefreshing(false);
    }
  }, []);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchTVDashboard(true);

    const refreshInterval = setInterval(() => {
      fetchTVDashboard(false);
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [fetchTVDashboard]);

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Helper Functions
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return `₹${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return <ArrowUpRight className="w-4 h-4" />;
    if (trend === "down") return <ArrowDownRight className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getTrendColor = (trend) => {
    if (trend === "up") return "text-success";
    if (trend === "down") return "text-destructive";
    return "text-muted-foreground";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "preparing":
        return "bg-warning/20 text-warning-foreground border-warning";
      case "ready":
        return "bg-success/20 text-success-foreground border-success";
      case "delivered":
        return "bg-primary/20 text-primary-foreground border-primary";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "critical":
        return <AlertCircle className="w-4 h-4" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4" />;
      case "success":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getAlertClass = (type) => {
    switch (type) {
      case "critical":
        return "bg-destructive/10 border-l-4 border-destructive";
      case "warning":
        return "bg-warning/10 border-l-4 border-warning";
      case "success":
        return "bg-success/10 border-l-4 border-success";
      default:
        return "bg-primary/10 border-l-4 border-primary";
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error && !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center space-y-4">
            <WifiOff className="w-12 h-12 text-destructive mx-auto" />
            <h2 className="text-xl font-bold">Connection Error</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={() => fetchTVDashboard(true)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
              <UtensilsCrossed className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {restroName || "Restaurant"}
              </h1>
              <p className="text-sm text-muted-foreground">
                Live Dashboard • Auto-refresh 30s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${
                connectionStatus === "online"
                  ? "bg-success/20 text-success"
                  : connectionStatus === "offline"
                    ? "bg-destructive/20 text-destructive"
                    : "bg-warning/20 text-warning"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  connectionStatus === "online"
                    ? "bg-success animate-pulse"
                    : connectionStatus === "offline"
                      ? "bg-destructive"
                      : "bg-warning animate-pulse"
                }`}
              />
              {connectionStatus === "online"
                ? "LIVE"
                : connectionStatus === "offline"
                  ? "OFFLINE"
                  : "ERROR"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-3xl font-bold text-foreground tabular-nums">
              {currentTime.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {currentTime.toLocaleDateString("en-IN", {
                weekday: "long",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTVDashboard(true)}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
            <Button variant="outline" size="icon" onClick={toggleFullscreen}>
              {isFullscreen ? (
                <Minimize className="w-4 h-4" />
              ) : (
                <Maximize className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Connection Error Banner */}
      {error && (
        <Alert className="mb-6 border-warning bg-warning/10">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Sales Overview */}
        <div className="col-span-8 space-y-6">
          {/* Sales Cards */}
          <div className="grid grid-cols-4 gap-4">
            {/* Today's Sales */}
            <Card className="relative overflow-hidden border-2 hover:border-primary/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground text-sm font-medium">
                    Today's Sales
                  </span>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(data?.todaySales)}
                </p>
                <div
                  className={`flex items-center gap-1 mt-2 text-sm ${getTrendColor(
                    data?.salesTrend,
                  )}`}
                >
                  {getTrendIcon(data?.salesTrend)}
                  <span className="font-medium">
                    {Math.abs(data?.salesChange || 0)}% vs yesterday
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Dine In */}
            <Card className="relative overflow-hidden border-2 hover:border-chart-3/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground text-sm font-medium">
                    Dine In
                  </span>
                  <div className="w-10 h-10 rounded-full bg-chart-3/10 flex items-center justify-center">
                    <UtensilsCrossed className="w-5 h-5 text-chart-3" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(data?.dineIn)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data?.dineInOrders || 0} orders
                </p>
              </CardContent>
            </Card>

            {/* Takeaway */}
            <Card className="relative overflow-hidden border-2 hover:border-chart-4/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground text-sm font-medium">
                    Takeaway
                  </span>
                  <div className="w-10 h-10 rounded-full bg-chart-4/10 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-chart-4" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(data?.takeaway)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data?.takeawayOrders || 0} orders
                </p>
              </CardContent>
            </Card>

            {/* Delivery */}
            <Card className="relative overflow-hidden border-2 hover:border-chart-5/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground text-sm font-medium">
                    Delivery
                  </span>
                  <div className="w-10 h-10 rounded-full bg-chart-5/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-chart-5" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(data?.delivery)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data?.deliveryOrders || 0} orders
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Best Sellers & Recent Orders */}
          <div className="grid grid-cols-2 gap-6">
            {/* Best Sellers */}
            <Card className="border-2">
              <CardHeader className="pb-4 border-b">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-primary" />
                  Best Sellers Today
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {data?.bestSellers && data.bestSellers.length > 0 ? (
                  <div className="space-y-4">
                    {data.bestSellers.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <span
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0
                              ? "bg-amber-500 text-white"
                              : index === 1
                                ? "bg-slate-400 text-white"
                                : index === 2
                                  ? "bg-orange-600 text-white"
                                  : "bg-primary/10 text-primary"
                          }`}
                        >
                          {item.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {item.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item.orders} orders sold
                          </p>
                        </div>
                        <p className="font-semibold text-foreground">
                          {formatCurrency(item.revenue)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No sales data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card className="border-2">
              <CardHeader className="pb-4 border-b">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Recent Orders
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {data?.recentOrders && data.recentOrders.length > 0 ? (
                  <div className="space-y-3">
                    {data.recentOrders.map((order, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-foreground">
                              {order.id}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {order.orderType}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {order.table} • {order.items} items • {order.time}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(order.status)}>
                            {order.status}
                          </Badge>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {formatCurrency(order.amount)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No recent orders</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {data?.totalOrders || 0}
                </p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(data?.avgOrderValue)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Order</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-success">
                  {data?.completedOrders || 0}
                </p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-warning">
                  {data?.activeOrders || 0}
                </p>
                <p className="text-sm text-muted-foreground">Active Now</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Column - Stats & Alerts */}
        <div className="col-span-4 space-y-6">
          {/* Restaurant Status */}
          <Card className="border-2">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-semibold">
                Restaurant Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Tables */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    Tables Occupied
                  </span>
                  <span className="font-bold text-foreground">
                    {data?.tablesOccupied || 0}/{data?.totalTables || 0}
                  </span>
                </div>
                <Progress value={data?.tablesOccupancy || 0} className="h-3" />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {data?.tablesOccupancy || 0}% occupancy
                </p>
              </div>

              {/* Staff */}
              {/* <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    Staff Present
                  </span>
                  <span className="font-bold text-foreground">
                    {data?.staffPresent || 0}/{data?.totalStaff || 0}
                  </span>
                </div>
                <Progress value={data?.staffPresence || 0} className="h-3" />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {data?.staffPresence || 0}% present
                </p>
              </div> */}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 pt-4 border-t">
                <div className="text-center p-3 rounded-lg bg-warning/10">
                  <p className="text-2xl font-bold text-warning">
                    {data?.kitchenPending || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Kitchen Pending
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-destructive/10">
                  <p className="text-2xl font-bold text-destructive">
                    {data?.lowStockItems || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Low Stock</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-primary/10">
                  <p className="text-2xl font-bold text-primary">
                    {data?.pendingPOs || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Pending POs</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-chart-3/10">
                  <p className="text-2xl font-bold text-chart-3">
                    {data?.grnPending || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">GRN Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts */}
          <Card className="border-2">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warning" />
                Alerts & Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {data?.alerts && data.alerts.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {data.alerts.slice(0, 8).map((alert, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${getAlertClass(
                        alert.type,
                      )} flex items-start gap-3`}
                    >
                      {getAlertIcon(alert.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {alert.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {alert.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-success opacity-50" />
                  <p>All clear!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Food Cost */}
          <Card className="border-2">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Cost Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Food Cost %
                </span>
                <span className="font-bold text-lg text-foreground">
                  {data?.foodCostPercent || 0}%
                </span>
              </div>
              <Progress value={data?.foodCostPercent || 0} className="h-3" />
              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Discount</span>
                  <span className="font-medium">
                    {formatCurrency(data?.totalDiscount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Tax</span>
                  <span className="font-medium">
                    {formatCurrency(data?.totalTax)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <p>
            Last updated:{" "}
            {lastRefresh.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
          <Badge variant="outline" className="text-xs">
            Auto-refresh: 30s
          </Badge>
        </div>
        <p className="text-xs">
          {data?.timestamp &&
            `Server time: ${new Date(data.timestamp).toLocaleTimeString(
              "en-IN",
            )}`}
        </p>
      </div>
    </div>
  );
}
